import request, { requestFile, requestFormData, ssoRequest, requestWithoutAuth, requestMediaFile } from './request';
import serverRequest from './serverRequest';

import {
    baseProgress,
    gtmProgress,
    transitProgress,
    stateProgress,
    debitProgress,
    voronoiProgress,
} from '@/components/wasm/lib';

const api = {
    system: {
        getServerVersion: () => request.get(`server_version/`).json(),
    },
    user: {
        login: data =>
            requestWithoutAuth
                .post('token/', {
                    hooks: {
                        beforeRequest: [
                            r => {
                                r.headers.delete('Authorization');
                            },
                        ],
                        // Тут можно перехватить ошибку!
                        afterResponse: [],
                    },
                    json: data,
                })
                .json(),
        ldap: () => ssoRequest.get(process.env.SSO_URL).json(),
        session: token => serverRequest(token).get('users/me/').json(),
        sessionClient: token =>
            requestWithoutAuth
                .get('users/me/', {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                })
                .json(),
        saveData: ({ key, data }) => request.post(`users/save/`, { body: JSON.stringify({ key, data }) }).json(),
        loadData: key => request.get(`users/load/?key=${key}`).json(),
        clearData: () => request.post(`users/clear/`).json(),
    },
    tree: {
        fetchData: ({ params, groupView }) => request.get(`tree-info/${groupView}/${params || ''}`).json(),
        treeSearch: (keyWord, searchMode, treeViewBy) =>
            request
                .post(`full-text-search/`, {
                    body: JSON.stringify({ searchString: keyWord, searchMode: searchMode, treeViewBy: treeViewBy }),
                })
                .json(),
        fetchTreeData: () => request.get(`tree-info/fields-list/`).json(),
        fetchExtSearch: ({ searchList, fieldId, areaId, elevationId, organizationId }) =>
            request
                .post(`tree-info/wells-group-search/`, {
                    body: JSON.stringify({
                        searchList,
                        fieldId: fieldId,
                        areaId: areaId,
                        elevationId: elevationId,
                        organizationId,
                    }),
                })
                .json(),
        fetchExtSearchById: searchList =>
            request.post(`tree-info/wells-id-search/`, { body: JSON.stringify({ wellId: searchList }) }).json(),
        fetchWellById: searchList =>
            request.post(`tree-info/wells-id-search/`, { body: JSON.stringify({ wellId: searchList }) }),
        fetchNews: () => request.get(`news/`).json(),
    },
    carottage: {
        fetchGeoObjects: (body = {}) =>
            request.post(`logging/get-rigis-and-fgi/`, { body: JSON.stringify(body) }).json(),
        fetchRigisList: (body = {}) =>
            request.post(`logging/get-manager-rigis/`, { body: JSON.stringify(body) }).json(),
        fetchRigisData: (body = {}) => request.post(`logging/reports/`, { body: JSON.stringify(body) }).json(),
        fetchResearch: id => request.get(`logging/load-research/?params={"well": ${id}}`).json(),
        fetchColumns: () => request.get(`logging/column-settings/`).json(),
        fetchGisData: () => request.get(`logging/curve-settings-gis/`).json(),
        fetchCurveData: data => request.get(`logging/curve-data/?params={"curves": ${data}}`).json(),
        fetchCurveMinMax: curve => request.get(`logging/curve-min-max/?params={"curve": ${curve}}`).json(),
        fetchCurvesByCode: ids =>
            request.post(`logging/map-code-curve/`, { body: JSON.stringify({ wellsId: ids }) }).json(),
        fetchFlowMeteringData: id => request.get(`logging/flow-metering-data/?params={"well": ${id}}`).json(),
        fetchTemplates: () => request.get(`logging/logging-templates/`).json(),
        fetchGdi: body => request.post('logging/well-gdi/', { body: JSON.stringify(body) }).json(),
        fetchFaces: body => request.post('logging/well-bottomholes/', { body: JSON.stringify(body) }).json(),
        fetchOffsets: body => request.post('logging/well-offsets/', { body: JSON.stringify(body) }).json(),
        fetchOffsetVersions: () => request.get('logging/offsets-versions/').json(),
        fetchVersionWellPacks: body => request.post(`logging/version-well-packs/`, { body }).json(),
        createWellsRappers: body => request.post(`logging/add-well-packs/`, { body }).json(),
        fetchProfilesGroupes: () => request.get(`logging/group-profiles-list/`).json(),
        fetchProfilesData: body => request.post(`logging/profile-data/`, { body: JSON.stringify(body) }).json(),
        saveProfile: body => request.post(`logging/profile/`, { body }).json(),
        deleteProfile: body => request.delete(`logging/profile/`, { body: JSON.stringify(body) }).json(),
        fetchCementometry: body => request.post(`logging/well-cementometry/`, { body: JSON.stringify(body) }).json(),
        fetchCore: body => request.post(`logging/well-core/`, { body: JSON.stringify(body) }).json(),
        fetchStratigraphy: body => request.post(`logging/stratigraphy/`, { body: JSON.stringify(body) }).json(),
        fetchSectionCasing: body => request.post(`logging/bg/section-casing/`, { body: JSON.stringify(body) }).json(),
        fetchCementLogging: body => request.post(`logging/bg/cement-logging/`, { body: JSON.stringify(body) }).json(),
        fetchIsolation: body => request.post(`logging/bg/isolation/`, { body: JSON.stringify(body) }).json(),
        fetchDrilling: body => request.post(`logging/bg/drilling/`, { body: JSON.stringify(body) }).json(),
        fetchThicknessInterlayer: body =>
            request.post(`logging/bg/thickness-interlayer/`, { body: JSON.stringify(body) }).json(),
        fetchFgiData: body => request.post(`logging/reports-fgi-530/`, { body: JSON.stringify(body) }),
        fetchFgiList: body => request.post(`logging/get-manager-fgi-530/`, { body: JSON.stringify(body) }).json(),
        fetchCoreSample: body => request.post(`logging/bg/core-sample/`, { body: JSON.stringify(body) }).json(),
        fetchInnk: body => request.post(`logging/bg/innk-ign-control/`, { body: JSON.stringify(body) }).json(),
        fetchTracers: body => request.post(`logging/bg/tracer-research/`, { body: JSON.stringify(body) }).json(),
        getCustomGeoVersions: wellsId => request.get(`logging/custom-rigis-version/?wellsId=${wellsId}`).json(),
        getAllCustomGeoVersions: fieldId => request.get(`logging/custom-rigis-version/?fieldId=${fieldId}`).json(),
        getAllMinesAndWells: fieldId => request.get(`logging/get-all-mines-and-wells/?fieldId=${fieldId}`).json(),
        getAllWellVersions: wellId => request.get(`logging/get-all-versions/?wellId=${wellId}`).json(),
        saveCustomGeoVersion: body =>
            request.post(`logging/custom-rigis-version/`, { body: JSON.stringify(body) }).json(),
        deleteCustomGeoVersion: id => request.delete(`logging/custom-rigis-version/?id=${id}`).json(),
        updateCustomGeoVersion: (id, body) =>
            request
                .put(`logging/custom-rigis-version/?id=${id}`, {
                    json: body,
                })
                .json(),
    },
    mapGeneration: {
        barrelCharacteristics: () => request.get(`map-data/type-wellbore/`).json(),
        fetchStratums: fieldId =>
            request.post(`map-data/field-stratums/`, { body: JSON.stringify({ field: fieldId }) }).json(),
        fetchWells: (fieldId, stratumIds) =>
            request
                .post(`map-data/field-wells/`, {
                    onDownloadProgress: (prog, chunk) => {
                        // Example output:
                        // `0% - 0 of 1271 bytes`
                        // `100% - 1271 of 1271 bytes`
                        // console.log(
                        //     `${progress.percent * 100}% - ${progress.transferredBytes} of ${progress.totalBytes} bytes`
                        // );
                        const percent = Math.round(Math.min(1, prog.transferredBytes / (prog.totalBytes * 3)) * 100);
                        baseProgress.setPercent(percent);
                    },
                    body: JSON.stringify({ field: fieldId, stratums: stratumIds }),
                })
                .json(),
        fetchWellByNode: body => {
            return request.post(`map/wells-by-node/`, {
                onDownloadProgress: (prog, chunk) => {
                    // Example output:
                    // `0% - 0 of 1271 bytes`
                    // `100% - 1271 of 1271 bytes`
                    // console.log(
                    //     `${progress.percent * 100}% - ${progress.transferredBytes} of ${progress.totalBytes} bytes`
                    // );
                    const percent = Math.round(Math.min(1, prog.transferredBytes / (prog.totalBytes * 3)) * 100);
                    baseProgress.setPercent(percent);
                },
                hooks: {
                    beforeRequest: [
                        r => {
                            r.headers.set('Accept', 'application/json, application/cbor, application/zip');
                            r.headers.set('Content-type', 'application/cbor');
                        },
                    ],
                    // Тут можно перехватить ошибку!
                    afterResponse: [],
                },
                body: JSON.stringify(body),
            });
        },
        fetchWellByNodeByMedia: file => {
            return requestMediaFile.get(`${process.env.NEXT_PUBLIC_MEDIA_URL}/media/${file}`, {
                // onDownloadProgress: (prog, chunk) => {
                //     // Example output:
                //     // `0% - 0 of 1271 bytes`
                //     // `100% - 1271 of 1271 bytes`
                //     // console.log(
                //     //     `${progress.percent * 100}% - ${progress.transferredBytes} of ${progress.totalBytes} bytes`
                //     // );
                //     const percent = Math.round(Math.min(1, prog.transferredBytes / (prog.totalBytes * 3)) * 100);
                //     baseProgress.setPercent(percent);
                // },
                hooks: {
                    beforeRequest: [
                        r => {
                            r.headers.set('Accept', 'application/json, application/cbor, application/zip');
                            r.headers.set('Content-type', 'application/cbor');
                        },
                    ],
                    // Тут можно перехватить ошибку!
                    afterResponse: [],
                },
            });
        },
        fetchWellsLite: ({ body = {}, signal }) =>
            request.post(`map/field-wells-lite/`, {
                onDownloadProgress: (prog, chunk) => {
                    // Example output:
                    // `0% - 0 of 1271 bytes`
                    // `100% - 1271 of 1271 bytes`
                    // console.log(
                    //     `${progress.percent * 100}% - ${progress.transferredBytes} of ${progress.totalBytes} bytes`
                    // );
                    const percent = Math.round(Math.min(1, prog.transferredBytes / (prog.totalBytes * 3)) * 100);
                    baseProgress.setPercent(percent);
                },
                hooks: {
                    beforeRequest: [
                        request => {
                            request.headers.set('Accept', 'application/json, application/cbor, application/zip');
                            request.headers.set('Content-type', 'application/cbor');
                        },
                    ],
                    // Тут можно перехватить ошибку!
                    afterResponse: [],
                },
                body: JSON.stringify(body),
                signal,
            }),
        fetchProduction: (stratumIds, wellIds, version) =>
            request
                .post(`map-data/wells/map-production/`, {
                    body: JSON.stringify({ stratums: stratumIds, wells: wellIds, version: version }),
                })
                .json(),
        fetchProductionCBOR: (stratumIds, fieldId, versions, cbor) =>
            request.post(`map/production/`, {
                onDownloadProgress: (progress, chunk) => {
                    // Example output:
                    // `0% - 0 of 1271 bytes`
                    // `100% - 1271 of 1271 bytes`
                    // console.log(
                    //     `${progress.percent * 100}% - ${progress.transferredBytes} of ${progress.totalBytes} bytes`
                    // );
                },
                hooks: {
                    beforeRequest: [
                        r => {
                            r.headers.set('Accept', 'application/json, application/cbor, application/zip');
                            r.headers.set('Content-type', 'application/cbor');
                        },
                    ],
                    // Тут можно перехватить ошибку!
                    afterResponse: [],
                },
                body: JSON.stringify({
                    stratums: stratumIds,
                    field: fieldId,
                    versions: versions,
                    cbor: cbor,
                }),
            }),
        fetchProductionZIP: (stratumIds, fieldId, versions, cbor, chunkSize) => {
            return request.post(`map/production/`, {
                onDownloadProgress: (prog, chunk) => {
                    // Example output:
                    // `0% - 0 of 1271 bytes`
                    // `100% - 1271 of 1271 bytes`
                    // console.log(
                    //     `${progress.percent * 100}% - ${progress.transferredBytes} of ${progress.totalBytes} bytes`
                    // );
                    // const percent = Math.round(Math.min(1, prog.transferredBytes / (prog.totalBytes * 3)) * 100);
                    // console.log(`${percent}% - ${progress.transferredBytes} of ${progress.totalBytes} bytes`);
                    // progress.setPercent(percent);
                },
                hooks: {
                    beforeRequest: [
                        r => {
                            r.headers.set('Accept', 'application/json, application/cbor, application/zip');
                            r.headers.set('Content-type', 'application/zip');
                        },
                    ],
                    // Тут можно перехватить ошибку!
                    afterResponse: [],
                },
                body: JSON.stringify({
                    stratums: stratumIds,
                    field: fieldId,
                    versions: versions,
                    cbor: cbor,
                    chunkSize: chunkSize,
                }),
            });
        },
        /**
         * Fetches the production ZIP file.
         *
         * @param {Object} body - The body of the request.
         * @param {Array<number>} body.stratums - The stratums IDs array.
         * @param {string} body.field - The field ID value.
         * @param {Array<number>} [body.wells] - The optional wells IDs array.
         * @param {Array} body.versions - The versions IDs array.
         * @param {boolean} body.cbor - The cbor flag.
         * @param {number} body.chunkSize - The chunk size value.
         * @param {AbortSignal} signal - The signal used to abort the request.
         * @return {Promise} A promise that resolves with the response of the request.
         */
        fetchProductionZIP_v2: (body, signal) => {
            return request.post(`map/production-v2/`, {
                onDownloadProgress: (prog, chunk) => {
                    // Example output:
                    // `0% - 0 of 1271 bytes`
                    // `100% - 1271 of 1271 bytes`
                    // console.log(
                    //     `${progress.percent * 100}% - ${progress.transferredBytes} of ${progress.totalBytes} bytes`
                    // );

                    const percent = Math.round(Math.min(1, prog.transferredBytes / (prog.totalBytes * 3)) * 100);
                    // console.log(`${percent}% - ${progress.transferredBytes} of ${progress.totalBytes} bytes`);
                    // progress.setPercent(percent);
                    debitProgress.setPercent(percent);
                },
                hooks: {
                    beforeRequest: [
                        r => {
                            r.headers.set('Accept', 'application/json, application/cbor, application/zip');
                            r.headers.set('Content-type', 'application/zip');
                        },
                    ],
                    // Тут можно перехватить ошибку!
                    afterResponse: [],
                },
                body: JSON.stringify(body),
                signal,
            });
        },
        fetchProductionBASE64: (stratumIds, fieldId, versions, cbor, chunkSize) => {
            return request.post(`map/production-v2/`, {
                onDownloadProgress: (prog, chunk) => {
                    // Example output:
                    // `0% - 0 of 1271 bytes`
                    // `100% - 1271 of 1271 bytes`
                    // console.log(
                    //     `${progress.percent * 100}% - ${progress.transferredBytes} of ${progress.totalBytes} bytes`
                    // );

                    const percent = Math.round(Math.min(1, prog.transferredBytes / (prog.totalBytes * 3)) * 100);
                    // console.log(`${percent}% - ${progress.transferredBytes} of ${progress.totalBytes} bytes`);
                    // progress.setPercent(percent);
                    debitProgress.setPercent(percent);
                },
                hooks: {
                    beforeRequest: [
                        r => {
                            r.headers.set(
                                'Accept',
                                'application/json, application/cbor, application/zip, application/x-tar',
                            );
                            r.headers.set('Content-type', 'application/cbor');
                        },
                    ],
                    // Тут можно перехватить ошибку!
                    afterResponse: [],
                },
                body: JSON.stringify({
                    stratums: stratumIds,
                    field: fieldId,
                    versions: versions,
                    cbor: cbor,
                    chunkSize: chunkSize,
                }),
            });
        },
        fetchGtmCBOR: (body, cbor) =>
            request.post(`map/gtm/`, {
                onDownloadProgress: (prog, chunk) => {
                    // Example output:
                    // `0% - 0 of 1271 bytes`
                    // `100% - 1271 of 1271 bytes`
                    // console.log(
                    //     `${progress.percent * 100}% - ${progress.transferredBytes} of ${progress.totalBytes} bytes`
                    // );
                    const percent = Math.round(Math.min(1, prog.transferredBytes / (prog.totalBytes * 3)) * 100);
                    gtmProgress.setPercent(percent);
                },
                hooks: {
                    beforeRequest: [
                        r => {
                            r.headers.set('Accept', 'application/json, application/cbor');
                            r.headers.set('Content-type', 'application/cbor');
                        },
                    ],
                    // Тут можно перехватить ошибку!
                    afterResponse: [],
                },
                body: JSON.stringify({ ...body, cbor: cbor }),
            }),
        /**
         * Fetches the production transit ZIP file.
         *
         * @param {Object} body - The body of the request.
         * @param {Array<number>} body.stratums - The stratums IDs array.
         * @param {string} body.field - The field ID value.
         * @param {Array<number>} [body.wells] - The optional wells IDs array.
         * @param {Array} body.versions - The versions IDs array.
         * @param {boolean} body.cbor - The cbor flag.
         * @param {AbortSignal} signal - The signal used to abort the request.
         * @return {Promise} A promise that resolves with the response of the request.
         */
        fetchProductionTransitZIP: (body, signal) =>
            request.post(`map/production-transit-v3/`, {
                onDownloadProgress: (prog, chunk) => {
                    // Example output:
                    // `0% - 0 of 1271 bytes`
                    // `100% - 1271 of 1271 bytes`
                    // console.log(
                    //     `${progress.percent * 100}% - ${progress.transferredBytes} of ${progress.totalBytes} bytes`
                    // );
                    const percent = Math.round(Math.min(1, prog.transferredBytes / (prog.totalBytes * 3)) * 100);
                    transitProgress.setPercent(percent);
                },
                hooks: {
                    beforeRequest: [
                        r => {
                            r.headers.set('Accept', 'application/json, application/cbor');
                            r.headers.set('Content-type', 'application/cbor');
                        },
                    ],
                    // Тут можно перехватить ошибку!
                    afterResponse: [],
                },
                body: JSON.stringify(body),
                signal,
            }),
        fetchProductionTransitZIPold: (body, signal) =>
            request.post(`map/production-transit/`, {
                onDownloadProgress: (prog, chunk) => {
                    // Example output:
                    // `0% - 0 of 1271 bytes`
                    // `100% - 1271 of 1271 bytes`
                    // console.log(
                    //     `${progress.percent * 100}% - ${progress.transferredBytes} of ${progress.totalBytes} bytes`
                    // );
                    const percent = Math.round(Math.min(1, prog.transferredBytes / (prog.totalBytes * 3)) * 100);
                    transitProgress.setPercent(percent);
                },
                hooks: {
                    beforeRequest: [
                        r => {
                            r.headers.set('Accept', 'application/json, application/cbor');
                            r.headers.set('Content-type', 'application/cbor');
                        },
                    ],
                    // Тут можно перехватить ошибку!
                    afterResponse: [],
                },
                body: JSON.stringify(body),
                signal,
            }),
        fetchProductionTransitBASE64: (stratumIds, fieldId, versions, cbor, zip) =>
            request.post(`map/production-transit/`, {
                onDownloadProgress: (prog, chunk) => {
                    // Example output:
                    // `0% - 0 of 1271 bytes`
                    // `100% - 1271 of 1271 bytes`
                    // console.log(
                    //     `${progress.percent * 100}% - ${progress.transferredBytes} of ${progress.totalBytes} bytes`
                    // );
                    const percent = Math.round(Math.min(1, prog.transferredBytes / (prog.totalBytes * 3)) * 100);
                    transitProgress.setPercent(percent);
                },
                hooks: {
                    beforeRequest: [
                        r => {
                            r.headers.set('Accept', 'application/json, application/cbor, application/x-tar');
                            r.headers.set('Content-type', 'application/cbor');
                        },
                    ],
                    // Тут можно перехватить ошибку!
                    afterResponse: [],
                },
                body: JSON.stringify({
                    stratums: stratumIds,
                    field: fieldId,
                    versions: versions,
                    cbor: cbor,
                    zip: zip,
                }),
            }),
        fetchStatesCBOR: (body, cbor) =>
            request.post(`map/states/`, {
                onDownloadProgress: (prog, chunk) => {
                    // Example output:
                    // `0% - 0 of 1271 bytes`
                    // `100% - 1271 of 1271 bytes`
                    // console.log(
                    //     `${progress.percent * 100}% - ${progress.transferredBytes} of ${progress.totalBytes} bytes`
                    // );
                    const percent = Math.round(Math.min(1, prog.transferredBytes / (prog.totalBytes * 3)) * 100);
                    stateProgress.setPercent(percent);
                },
                hooks: {
                    beforeRequest: [
                        r => {
                            r.headers.set('Accept', 'application/json, application/cbor');
                            r.headers.set('Content-type', 'application/cbor');
                        },
                    ],
                    // Тут можно перехватить ошибку!
                    afterResponse: [],
                },
                body: JSON.stringify({ ...body, cbor: cbor }),
            }),
        fetchContoursCBOR: (body, cbor) =>
            request.post(`map/contours/`, {
                onDownloadProgress: (prog, chunk) => {
                    // Example output:
                    // `0% - 0 of 1271 bytes`
                    // `100% - 1271 of 1271 bytes`
                    // console.log(
                    //     `${progress.percent * 100}% - ${progress.transferredBytes} of ${progress.totalBytes} bytes`
                    // );
                    const percent = Math.round(Math.min(1, prog.transferredBytes / (prog.totalBytes * 3)) * 100);
                    stateProgress.setPercent(percent);
                },
                hooks: {
                    beforeRequest: [
                        r => {
                            r.headers.set('Accept', 'application/json, application/cbor');
                            r.headers.set('Content-type', 'application/cbor');
                        },
                    ],
                    // Тут можно перехватить ошибку!
                    afterResponse: [],
                },
                body: JSON.stringify({
                    ...body,
                    cbor: cbor,
                }),
            }),
        fetchVoronoiCBOR: (fieldId, stratumIds, versions, prodVersion, cbor) =>
            request.post(`map/voronoi/`, {
                onDownloadProgress: (prog, chunk) => {
                    // Example output:
                    // `0% - 0 of 1271 bytes`
                    // `100% - 1271 of 1271 bytes`
                    // console.log(
                    //     `${progress.percent * 100}% - ${progress.transferredBytes} of ${progress.totalBytes} bytes`
                    // );
                    const percent = Math.round(Math.min(1, prog.transferredBytes / (prog.totalBytes * 3)) * 100);
                    voronoiProgress.setPercent(percent);
                },
                hooks: {
                    beforeRequest: [
                        r => {
                            r.headers.set('Accept', 'application/json, application/cbor');
                            r.headers.set('Content-type', 'application/cbor');
                        },
                    ],
                    // Тут можно перехватить ошибку!
                    afterResponse: [],
                },
                body: JSON.stringify({
                    fieldid: fieldId,
                    stratums: stratumIds,
                    versions: versions,
                    prodVersion: prodVersion,
                    cbor: cbor,
                }),
            }),
        fetchGridParams: fieldId =>
            request
                .post(`map/grid-settings/`, {
                    onDownloadProgress: (prog, chunk) => {
                        // Example output:
                        // `0% - 0 of 1271 bytes`
                        // `100% - 1271 of 1271 bytes`
                        // console.log(
                        //     `${progress.percent * 100}% - ${progress.transferredBytes} of ${progress.totalBytes} bytes`
                        // );
                        const percent = Math.round(Math.min(1, prog.transferredBytes / (prog.totalBytes * 3)) * 100);
                        stateProgress.setPercent(percent);
                    },
                    hooks: {
                        beforeRequest: [
                            r => {
                                r.headers.set('Accept', 'application/json, application/cbor');
                                r.headers.set('Content-type', 'application/cbor');
                            },
                        ],
                        // Тут можно перехватить ошибку!
                        afterResponse: [],
                    },
                    body: JSON.stringify({
                        fieldId: fieldId,
                        cbor: false,
                    }),
                })
                .json(),
        fetchVersions: (fieldId, ventureId) =>
            request
                .post(`common/get-version/`, { body: JSON.stringify({ fieldId: fieldId, ventureId: ventureId }) })
                .json(),
        fetchGtm: fieldId =>
            request.post(`map-data/wells/gtm-map/`, { body: JSON.stringify({ fieldId: fieldId }) }).json(),
        fetchResourcesSettings: ({ body, signal }) =>
            request.post(`map/resources-settings/`, { body: JSON.stringify(body), signal }).json(),
        fetchResources: ({ body = {}, signal }) =>
            request.post(`map/resources/`, {
                hooks: {
                    beforeRequest: [
                        r => {
                            r.headers.set('Accept', 'application/json, application/cbor');
                            r.headers.set('Content-type', 'application/cbor');
                        },
                    ],
                    // Тут можно перехватить ошибку!
                    afterResponse: [],
                },
                body: JSON.stringify(body),
                signal,
            }),
        fetchRequiredInjection: ({ body, signal }) =>
            request.post(`map-data/injection-required/`, { body: JSON.stringify(body), signal }).json(),
        fetchGtmPrograms: ({ body, signal }) =>
            request.post(`map/gtm-program/`, { body: JSON.stringify(body), signal }).json(),
        fetchGtmProgramsInfo: ({ signal }) => request.get(`map/gtm-program-info/`, { signal }).json(),
        fetchGtmProgramFieldData: fieldId => request.get(`map/gtm-program-field-data/?fieldId=${fieldId}`).json(),
        saveGtmProgram: ({ body, signal }) =>
            request.post(`map/save-gtm-program/`, { body: JSON.stringify(body), signal }).json(),
        deleteGtmProgram: id => request.get(`map/delete-gtm-program/?id=${id}`).json(),
        fetchShapeFiles: signal => request.get(`map/shape-list/`, { signal }).json(),
        fetchShapeFileById: (body, signal) =>
            request.post(`map/shape/`, {
                hooks: {
                    beforeRequest: [
                        r => {
                            r.headers.set('Accept', 'application/json, application/cbor');
                            r.headers.set('Content-type', 'application/cbor');
                        },
                    ],
                    // Тут можно перехватить ошибку!
                    afterResponse: [],
                },
                body: JSON.stringify(body),
                signal,
            }),
        deleteShapeFile: id => request.get(`map/delete-shape/?id=${id}`).json(),
        uploadShape: body => requestFormData.post(`map/upload-shape/`, { body }).json(),
        fetchShapeZip: id => request.get(`map/shape/?zip=true&id=${id}`),
    },
    productionInjection: {
        fetchData: ({ body, signal }) =>
            request.post(`production-injection/`, { body: JSON.stringify(body), signal }).json(),
        fetchDataGdis: ({ body, signal }) =>
            request.post(`production-injection/gdi/`, { body: JSON.stringify(body), signal }).json(),
        deleteCache: ({ body, signal }) =>
            request.post(`production-injection/delete_cache/`, { body: JSON.stringify(body), signal }).json(),
        // exportToExcel: ({ body, signal }) =>
        //     requestFile.post(`production-injection/export`, { body: JSON.stringify(body), signal }).json(),
    },
    miningAndPumping: {
        fetchVersionsForWell: ({ id }) => request.get(`production-upload/well-version-general/${id}/`).json(),
        fetchLayers: body =>
            request.post(`production-upload/version-stratum-reference/`, { body: JSON.stringify(body) }).json(),
        fetchLayersForField: body =>
            request
                .post(`production-upload/settings-summary/stratum-reference/`, { body: JSON.stringify(body) })
                .json(),
        fetchAxes: () => request.get(`production-upload/list-axes-new/`).json(),
        // fetchChartsCurves: ({ id, version }) =>
        //     request.get(`production-upload/diagram/${id}/${version}/?typeDiagram=keys`).json(),
        fetchIndicators: (filter = {}) =>
            request.post(`production-upload/reference-new/`, { body: JSON.stringify(filter) }).json(),
        // fetchIndicatorsList: (filter = {}) =>
        //     request.post(`production-upload/reference-new/`, { body: JSON.stringify(filter) }).json(),
        fetchWellFinalState: body =>
            request.post(`production-upload/well-final-state/`, { body: JSON.stringify(body) }).json(),
        fetchDataForCurve: ({ body, signal }) =>
            request.post(`production-upload/data-for-curve/`, { body: JSON.stringify(body), signal }).json(),
        fetchGtm: body => request.post(`production-upload/gtm-info/`, { body: JSON.stringify(body) }).json(),
        fetchIndicationStratums: body =>
            request.post(`production-upload/indication-stratums/`, { body: JSON.stringify(body) }).json(),
        fetchStateIcons: body => request.post(`map-data/wells/state-icons/`, { body: JSON.stringify(body) }).json(),
        fetchWellState: body => request.post(`map-data/wells/well-state/`, { body: JSON.stringify(body) }).json(),
        // fetchChartsDataSource: ({ id, version, curves }) =>
        //     request.get(`production-upload/diagram/${id}/${version}/?curves=${JSON.stringify(curves)}`).json(),
        fetchTableColumns: body =>
            request.post('production-upload/table-reference/', { body: JSON.stringify(body) }).json(),
        fetchTableColumnsForField: body =>
            request.post('production-upload/table-reference/summary/', { body: JSON.stringify(body) }).json(),
        fetchTableDataSource: ({ body, signal }) =>
            request.post(`production-upload/table-new/row/`, { body: JSON.stringify(body), signal }).json(),
        fetchDataSourceForField: ({ body, signal }) =>
            request.post(`production-upload/table-new/row/summary/`, { body: JSON.stringify(body), signal }).json(),
        fetchDistricts: data => request.post('map-data/', { json: data }).json(),
        fetchSettingsSummary: (body = {}) =>
            request.post('production-upload/settings-summary/', { body: JSON.stringify(body) }).json(),
        fetchGraphDataSummary: ({ json, signal }) =>
            request.post('production-upload/graph-data-summary/', { json, signal }).json(),
        deleteGraphDataSummaryCache: ({ json, signal }) =>
            request.post('production-upload/graph-data-summary/delete_cache/', { json, signal }).json(),
        fetchComparisonOfPeriods: ({ json, signal }) =>
            request.post('production-upload/comparison-of-periods/', { json, signal }).json(),
        fetchApproximationSummary: ({ json, signal }) =>
            request.post('production-upload/approximate-summary/', { json, signal }).json(),
        fetchConsolidatedProductionData: ({ json, signal }) =>
            request.post('production-upload/consolidated-production-data/', { json, signal }).json(),
        fetchConsolidatedProductionSettings: (body = {}) =>
            request.post('production-upload/consolidated-production-settings/', { body: JSON.stringify(body) }).json(),
        fetchSummaryIndicators: (body = {}) =>
            request.post('production-upload/graph-data-summary-reference/', { body: JSON.stringify(body) }).json(),
        fetchWellList: (body = {}) =>
            request.post('production-upload/well-list-summary/', { body: JSON.stringify(body) }).json(),
        fetchFieldStratums: body => request.post('map-data/field-stratums/', { body: JSON.stringify(body) }).json(),
    },
    gtmEfficiency: {
        fetchVersions: body => request.post(`common/get-version/`, { body: JSON.stringify(body) }).json(),
        fetchUserGtmSettings: body =>
            request.post(`gtm-efficiency/user-gtm-settings/`, { body: JSON.stringify(body) }).json(),
        fetchUserGtmWellData: body =>
            request.post(`gtm-efficiency/user-gtm-by-well/`, { body: JSON.stringify(body) }).json(),
        fetchCalculationId: params => request.get(`gtm-efficiency/get-gtm-id/?params=${params}`).json(),
        fetchCalculationData: params => request.get(`gtm-efficiency/get-gtm-data/?params=${params}`).json(),
        fetchCalculationDataFile: params => requestFile.get(`${params}`).json(),
        fetchCalculationTreeFile: params => requestFile.get(`${params}`).json(),
    },
    factorAnalysis: {
        fetchMiningVersions: (body = {}) =>
            request.post(`factor-analysis/mining-versions/`, { body: JSON.stringify(body) }).json(),
        fetchGtmTree: (body = {}) =>
            request.post('factor-analysis/gtm-list-tree/', { body: JSON.stringify(body) }).json(),
        fetchGtmFaTree: (body = {}) =>
            request.post('factor-analysis/gtm-fa-list-tree/', { body: JSON.stringify(body) }).json(),
        fetchWellsForCurrentTp: (body = {}) =>
            request.post('factor-analysis/fa-tp-wells/', { body: JSON.stringify(body) }).json(),
        fetchFactorAnalysisData: (body = {}) =>
            request.post('factor-analysis/get-data/', { body: JSON.stringify(body) }).json(),
    },
    oilProductionLoss: {
        fetchVersions: ({ body, signal }) =>
            request.post('common/get-version/', { body: JSON.stringify(body), signal }).json(),
        fetchStratums: ({ body, signal }) =>
            request.post('loss-oil-production/get-stratums/', { body: JSON.stringify(body), signal }).json(),
        fetchReportingPeriodAndGtm: ({ body, signal }) =>
            request.post('loss-oil-production/get-settings-data/', { body: JSON.stringify(body), signal }).json(),
        fetchLossOilProduction: ({ body = {}, signal }) =>
            request.post('loss-oil-production/get-loss-oil-production/', { body: JSON.stringify(body), signal }).json(),
        fetchData: ({ body = {}, signal }) =>
            request.post('loss-oil-production/', { body: JSON.stringify(body), signal }).json(),
        fetchDataForChart: ({ body = {}, signal }) =>
            request.post('loss-oil-production/all/', { body: JSON.stringify(body), signal }).json(),
    },

    gsr: {
        fetchDataset: ({ body = {}, signal }) => request.post(`gsr/`, { body: JSON.stringify(body), signal }).json(),
    },
    inclinometry: {
        get: wellID => request.get(`logging/well-inclinometry/?params={"well":${wellID}}`),
        getMany: wellIDs => request.get(`logging/wells-inclinometry/?params={"wells":[${wellIDs}]}`).json(),
    },
    well: {
        getMine: mineID => request.get(`wells-view/?params={"mine_id":${mineID}}`).json(),
        getWells: wellIDs => request.get(`wells-view/?params={"well_ids":[${wellIDs}]}`).json(),
    },
    desaturationCharacteristics: {
        fetchStratums: (body = {}) =>
            request.post('desaturation-characteristics/stratum/', { body: JSON.stringify(body) }).json(),
        fetchApproximation: (body = {}) =>
            request.post('desaturation-characteristics/approximate/', { body: JSON.stringify(body) }).json(),
    },
    reports: {
        get: (body, signal) => request.post('reports/tree/', { body: JSON.stringify(body), signal }).json(),
        getFieldIds: body => request.post('reports/get_field_ids/', { body: JSON.stringify(body) }).json(),
        getInquiryResearch: (wellId, signal) =>
            request.get(`reports/inquiry-research-list/?wellId=${wellId}`, { signal }).json(),
        getStratums: ({ body, signal }) =>
            request.post('reports/gos-plan-stratums/', { body: JSON.stringify(body), signal }).json(),
        getInterval: ({ body, signal }) =>
            request.post('reports/gos-plan-interval/', { body: JSON.stringify(body), signal }).json(),
        makeTriplets: body => request.post('reports/make-triplets-tempdb/', { body: JSON.stringify(body) }).json(),
        getGeoStratums: body => request.post('reports/get-geostratums/', { body: JSON.stringify(body) }).json(),
        fetchCoreReport: body => request.post(`reports/kern/`, { body: JSON.stringify(body) }).json(),
    },
    geologicProfiles: {
        getGeologicProfiles: body => request.post('logging/geologic-profile/', { body: JSON.stringify(body) }).json(),
    },
};

export default api;
